import { Button, Group, Loader, TextInput } from '@mantine/core';
import _debounce from 'lodash/debounce';
import { useEffect, useRef, useState } from 'react';

import { useValidateGoodsItemKey } from '../api/validateGoodsItemKey';
import { Fields, useGoodsItemForm } from '../hooks/useGoodsItemForm';

import { useKeyValidator } from '@/hooks/useKeyValidator';
import { Departments } from '@/types';
import transliteration from '@/utils/transliteration';

type GoodsItemFormProps = {
  isLoading: boolean;
  onSubmit: (params: Fields) => void;
  initialValues: Fields;
};

export const GoodsItemForm = ({ isLoading, onSubmit, initialValues }: GoodsItemFormProps) => {
  const form = useGoodsItemForm({ initialValues });
  const { mutateAsync: validateGoodsItem } = useValidateGoodsItemKey();
  const { validate } = useKeyValidator(validateGoodsItem);

  const [isTyping, setIsTyping] = useState(false);

  const debouncedValidateRef = useRef(
    _debounce(async (key: string, department: Departments) => {
      const error = await validate({ key, department });
      form.setFieldError('key', error);
      setIsTyping(false);
    }, 800)
  );

  useEffect(() => {
    const debounced = debouncedValidateRef.current;
    return () => {
      debounced.cancel();
    };
  }, []);

  const setKeyWithValidation = (key: string) => {
    form.setFieldValue('key', key);
    setIsTyping(true);
    debouncedValidateRef.current(key, form.values.department);
  };

  const handleNameChange = (name: string) => {
    form.setFieldValue('name', name);
    const generatedKey = transliteration.toKeyFormat(name);
    setKeyWithValidation(generatedKey);
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <TextInput
        label="Имя"
        autoComplete="off"
        required
        withAsterisk
        placeholder="Имя"
        value={form.values.name}
        onChange={(event) => handleNameChange(event.currentTarget.value)}
        mb="xl"
        error={form.errors.name}
      />
      <TextInput
        label="Ключ"
        autoComplete="off"
        required
        withAsterisk
        placeholder="Ключ"
        value={form.values.key}
        rightSection={isTyping && <Loader size="xs" />}
        onChange={(event) => setKeyWithValidation(event.currentTarget.value)}
        mb="xl"
        error={form.errors.key}
      />
      <TextInput
        label="Department"
        required
        readOnly
        {...form.getInputProps('department')}
        disabled
        mb="xl"
      />
      <Group justify="right">
        <Button
          loading={isLoading}
          type="submit"
          disabled={isTyping || isLoading || Object.keys(form.errors).length > 0}
        >
          Сохранить
        </Button>
      </Group>
    </form>
  );
};
